import { useEffect, useState } from "react";
import {
  FORTRESS_CARDS_CONFIG,
  type FortressCard,
  type MatchHistoryFilter,
  type MatchHistoryItem,
  type MatchHistoryResponse,
} from "@fortress/shared";
import { listMatchHistory } from "./auth/lobbies";
import { FortressCardFace } from "./library/FortressCardFace";

// The lobby hub's "My matches" tab: one page of the account's finished
// matches (GET /games/history), filterable by status, each row expandable
// into the full result — winner, the win-fortress card, rounds and dates.

/** Resolve a persisted `winCardId` to its catalogue card for rendering. The
 * bundled catalogue is fine here — a listing needn't pin to the match's
 * catalogue version the way a resume does. */
const CARD_BY_ID = new Map<string, FortressCard>(
  [
    ...(FORTRESS_CARDS_CONFIG.winCards as FortressCard[]),
    ...(FORTRESS_CARDS_CONFIG.regularCards as FortressCard[]),
  ].map((card) => [card.id, card]),
);

const FILTER_LABELS: Record<MatchHistoryFilter, string> = {
  completed: "Completed",
  canceled: "Canceled",
  all: "All",
};

/** Result badge for one row. "Ended" covers games finished before the winner
 * was persisted (older saves) — there is a result, we just don't have it. */
function resultBadge(match: MatchHistoryItem): { label: string; mod: string } {
  if (match.status === "canceled") return { label: "Canceled", mod: "canceled" };
  if (match.youWon) return { label: "Won", mod: "won" };
  if (match.winnerName !== null) return { label: "Lost", mod: "lost" };
  return { label: "Ended", mod: "ended" };
}

function shortDate(iso: string | null): string | null {
  return iso ? new Date(iso).toLocaleDateString() : null;
}

function fullDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : "—";
}

export function MatchHistory() {
  const [status, setStatus] = useState<MatchHistoryFilter>("completed");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<MatchHistoryResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    let stale = false;
    setBusy(true);
    setError(null);
    listMatchHistory({ status, page })
      .then((res) => {
        if (!stale) setData(res);
      })
      .catch(() => {
        if (!stale) setError("Couldn't load your matches. Try again.");
      })
      .finally(() => {
        if (!stale) setBusy(false);
      });
    return () => {
      stale = true;
    };
  }, [status, page]);

  const onFilter = (next: MatchHistoryFilter) => {
    setStatus(next);
    setPage(1);
    setExpandedId(null);
  };

  const matches = data?.matches ?? [];
  const totalPages = data
    ? Math.max(1, Math.ceil(data.total / data.pageSize))
    : 1;

  return (
    <section className="lobby-hub-panel lobby-hub-list">
      <div className="lobby-hub-list-head">
        <h2>My matches</h2>
        <select
          className="auth-input lobby-hub-history-filter"
          aria-label="Filter matches by status"
          value={status}
          onChange={(e) => onFilter(e.target.value as MatchHistoryFilter)}
        >
          {(Object.keys(FILTER_LABELS) as MatchHistoryFilter[]).map((f) => (
            <option key={f} value={f}>
              {FILTER_LABELS[f]}
            </option>
          ))}
        </select>
      </div>
      {error && <p className="auth-error">{error}</p>}
      {matches.length === 0 && !busy && !error && (
        <p className="lobby-hub-empty">
          {status === "completed"
            ? "No completed matches yet."
            : "No matches here yet."}
        </p>
      )}
      <ul className="lobby-hub-rows">
        {matches.map((match) => {
          const badge = resultBadge(match);
          const expanded = expandedId === match.gameId;
          const winCard = match.winCardId
            ? CARD_BY_ID.get(match.winCardId)
            : undefined;
          const finished = shortDate(match.finishedAt);
          return (
            <li key={match.gameId} className="lobby-hub-row lobby-hub-history-row">
              <button
                type="button"
                className="lobby-hub-history-toggle"
                aria-expanded={expanded}
                onClick={() =>
                  setExpandedId(expanded ? null : match.gameId)
                }
              >
                <span className={`lobby-hub-status is-${badge.mod}`}>
                  {badge.label}
                </span>
                <span className="lobby-hub-history-players">
                  {match.players.join(", ") || "Unknown players"}
                </span>
                {winCard && (
                  <span className="lobby-hub-history-card-name">
                    {winCard.name}
                  </span>
                )}
                {finished && (
                  <span className="lobby-hub-history-date">{finished}</span>
                )}
              </button>
              {expanded && (
                <div className="lobby-hub-history-details">
                  <dl className="lobby-hub-history-facts">
                    <dt>Players</dt>
                    <dd>{match.players.join(", ") || "—"}</dd>
                    <dt>Winner</dt>
                    <dd>
                      {match.status === "canceled"
                        ? "— (canceled)"
                        : match.winnerName
                          ? `${match.winnerName}${match.youWon ? " (you)" : ""}`
                          : "Not recorded"}
                    </dd>
                    <dt>Rounds</dt>
                    <dd>{match.roundNumber}</dd>
                    <dt>Started</dt>
                    <dd>{fullDate(match.startedAt)}</dd>
                    <dt>{match.status === "canceled" ? "Canceled" : "Finished"}</dt>
                    <dd>{fullDate(match.finishedAt)}</dd>
                  </dl>
                  {match.winCardId &&
                    (winCard ? (
                      <FortressCardFace
                        card={winCard}
                        style={{ maxWidth: 280 }}
                      />
                    ) : (
                      <p className="lobby-hub-empty">
                        Won with {match.winCardId}
                      </p>
                    ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {(totalPages > 1 || page > 1) && (
        <div className="lobby-hub-pager">
          <button
            type="button"
            className="lobby-hub-watch"
            disabled={busy || page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span className="lobby-hub-pager-label">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            className="lobby-hub-watch"
            disabled={busy || page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </section>
  );
}
